import { Component, ReactNode } from "react"
import { throttle } from "lodash"

interface IDynamicOverflowProps {
    list({ nodeRef }): any,
    children({
        visibleElements,
        overflowElements,
        containerRef,
        moreNodeRef
    }): ReactNode,
    throttle?: number
}

interface IDynamicOverflowState {
    numberOfVisibleElements: number
}

class DynamicOverflow extends Component<IDynamicOverflowProps, IDynamicOverflowState> {
    listNodes
    moreNode
    containerNode

    constructor(props) {
        super(props)


        this.state = {
            numberOfVisibleElements: Infinity
        }
    }

    componentDidMount() {
        /*
        * All elements in props.list are rendered on the screen.
        *
        * Then, the containerNode and tabNode are measured to calculate how many
        * elements we can display.
        *
        * If elements need to be hidden, everything is rerendered.
        */
        this.calculateSize()
    }

    calculateSize = () => {
        if (this.listNodes === undefined) return

        const containerWidth = this.containerNode && this.containerNode.clientWidth

        let moreNodeWidth = 0
        if (this.moreNode) {
            const moreNodeStyle = window.getComputedStyle(this.moreNode)
            const moreNodeMarginWidth = parseInt(moreNodeStyle.marginLeft) + parseInt(moreNodeStyle.marginRight)
            moreNodeWidth = this.moreNode.offsetWidth + moreNodeMarginWidth
        }

        let maximumChildrenAllowed = 0
        let nodeWidthSum = 0

        const listNodes = this.listNodes.filter(node => node !== null)
        for (let i = listNodes.length - 1; i >= 0; i--) {
            const node = listNodes[i]
            const nodeStyle = window.getComputedStyle(node)
            const nodeWidth = node.offsetWidth
            const nodeMarginWidth = parseInt(nodeStyle.marginLeft) + parseInt(nodeStyle.marginRight)

            nodeWidthSum += (nodeWidth + nodeMarginWidth)
            if (nodeWidthSum + moreNodeWidth < containerWidth) {
                maximumChildrenAllowed += 1
            }
        }


        const currentChildrenCount = this.listNodes.length

        let numberOfVisibleElements = Infinity
        if (currentChildrenCount > maximumChildrenAllowed) {
            // by default, one element is always shown
            numberOfVisibleElements = Math.max(maximumChildrenAllowed, 1)
        }

        this.setState({ numberOfVisibleElements })
    }

    handleResize = throttle(this.calculateSize, this.props.throttle)

    containerRef = node => {
        if (this.containerNode === undefined) {
            this.containerNode = node
        }
    }

    moreNodeRef = node => {
        if (this.moreNode === undefined) {
            this.moreNode = node
            this.calculateSize()
        }
    }

    nodeRef = node => {
        if (this.listNodes === undefined) {
            this.listNodes = []
        }
        this.listNodes.push(node)
        this.calculateSize()
    }

    render() {
        const { numberOfVisibleElements } = this.state
        const { list, children } = this.props
        const { containerRef, moreNodeRef, nodeRef } = this

        const elements = list({ nodeRef })
        const visibleElements = elements.slice(Math.max(elements.length - numberOfVisibleElements, 0))
        const overflowElements = elements.slice(0, elements.length - numberOfVisibleElements)

        return children({
            visibleElements,
            overflowElements,
            containerRef,
            moreNodeRef,
        })
    }
}

export default DynamicOverflow
